import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-9a2a29e2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "mx-auto" }
const _hoisted_2 = { class: "mb-8" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "text-3xl font-semibold text-gray-900 work-sans" }, "Vocabulary List", -1))
const _hoisted_4 = { class: "student-name-heading" }
const _hoisted_5 = {
  key: 0,
  class: "flex justify-center items-center min-h-[300px]"
}
const _hoisted_6 = {
  key: 1,
  class: "flex justify-center items-center min-h-[300px]"
}
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-xl text-gray-600" }, "No Lingo data available for this student yet.", -1))
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = { key: 2 }
const _hoisted_10 = { class: "dashboard-container" }
const _hoisted_11 = { class: "sidebar" }
const _hoisted_12 = { class: "sidebar__body" }
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "poppins" }, "Overview", -1))
const _hoisted_14 = { class: "arch-section" }
const _hoisted_15 = { class: "main-content" }
const _hoisted_16 = { class: "data-tables" }
const _hoisted_17 = { class: "table-container" }
const _hoisted_18 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "table-heading" }, [
  /*#__PURE__*/_createElementVNode("h3", null, "Vocabulary List")
], -1))
const _hoisted_19 = { class: "word" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "status-text" }
const _hoisted_22 = ["innerHTML"]

import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import ProgressStatsCard from "@/modules/main/components/manage/ProgressStatsCardComponent.vue";
import LingoProgressCircle from "@/modules/main/components/manage/LingoProgressCircleComponent.vue";
import LingoModal from "@/modules/main/components/manage/LingoModalComponent.vue";
import LingoDataTable from "@/modules/main/components/manage/LingoDataTableComponent.vue";
import i18n from "@/plugins/i18n";
import { useLingoStore } from "@/stores/lingoStore";
import { useLearnerStore } from '@/stores/learnerStore';
import LingoTooltipIcon from "@/modules/main/components/manage/LingoTooltipIconComponent.vue";
import getKcStatusTooltip from "@/modules/main/utils/kc";
import BuildingIcon from '@/assets/icons/building-05.svg?component';


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoStudentVocabularyListView',
  setup(__props) {

const route = useRoute();
const lingoStore = useLingoStore();
const learnerStore = useLearnerStore();

// Simple status label mapping (same semantics as other KC views)
const STATUS_LABELS: Record<string, string> = {
  not_yet_seen: "Not yet seen",
  seen: "Seen",
  learning: "Learning",
  practicing: "Reinforcing",
  mastered_prior_learning: "Mastered (Prior Learning)",
  mastered_new_learning: "Mastered (Gap Filled)",
  mastered: "Mastered"
};

/**
 * Return label or raw value for a KC status.
 * Usage: getKcData(item.status, 'label') or getKcData(item.status, 'key')
 */
function getKcData(status: string | null | undefined, type: "label" | "key" = "label") {
  const key = status == null ? "" : String(status);
  if (type === "key") return key;
  return STATUS_LABELS[key] ?? key ?? "Unknown";
}

//breadcrumbs
const pages = computed(() => {
  const base = [{ name: "Lingo Data", path: "/main/lingo-data" }];
  base.push({ 
    name: "Data for " + displayName.value, 
    path: "/main/lingo-data/student/" + studentId.value
  });
  base.push({
    name: `Vocabulary List`, 
    path: "#"
  });
  return base;
});

const showLevelModal = ref(false);
const showLevelIcon = ref(true);
const levelIcon = ref<string | null>(null);
const modalTitle = ref("");
const modalBodyHtml = ref("");
const activeKcForModal = ref<any | null>(null);

const cols = ref([
  { key: "word", label: "Learning Intention", sortable: true },
  { key: "level", label: "Level", sortable: true }
]);
// Keep the per-row status tooltip, but do not show a tooltip in the Status header.
cols.value.push({
  key: "status",
  label: "Status",
  sortable: true
});

// Activity KCs used for counts/progress calculations should reference vocabularyKcs
const activityKcs = computed(() => vocabularyKCs.value || []);

const statusCounts = computed(() => {
  const initial: Record<string, number> = {
    not_yet_seen: 0,
    seen: 0,
    learning: 0,
    practicing: 0,
    mastered_prior_learning: 0,
    mastered_new_learning: 0
  };

  return (activityKcs.value || []).reduce((counts, kc: any) => {
    const s = kc && kc.status;
    if (s && Object.prototype.hasOwnProperty.call(counts, s)) {
      counts[s] += 1;
    }
    return counts;
  }, { ...initial });
});

const totalKcs = computed(() => (activityKcs.value || []).length);

const masteredCount = computed(() => (statusCounts.value.mastered_prior_learning || 0) + (statusCounts.value.mastered_new_learning || 0));

const progressStats = computed(() => {
  const total = totalKcs.value || 1;
  return [
    {
      status: "not_yet_seen",
      label: "Not yet seen",
      count: statusCounts.value.not_yet_seen,
      percentage: (statusCounts.value.not_yet_seen / total) * 100,
      color: "#667085",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.not_yet_seen").toString()
    },
    {
      status: "seen",
      label: "Seen",
      count: statusCounts.value.seen,
      percentage: (statusCounts.value.seen / total) * 100,
      color: "#1B7442",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.seen").toString()
    },
    {
      status: "learning",
      label: "Learning",
      count: statusCounts.value.learning,
      percentage: (statusCounts.value.learning / total) * 100,
      color: "#1B7442",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.learning").toString()
    },
    {
      status: "practicing",
      label: "Reinforcing",
      count: statusCounts.value.practicing,
      percentage: (statusCounts.value.practicing / total) * 100,
      color: "#1B7442",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.reinforcing").toString()
    },
    {
      status: "mastered",
      label: "Mastered",
      count: masteredCount.value,
      percentage: (masteredCount.value / total) * 100,
      color: "#EAAA08",
      tooltip: i18n.global.t("lingo_v2.tooltips.kc_arch_progress_status.mastered").toString()
    }
  ];
});

function openLevelModalFor(kc: any) {
  activeKcForModal.value = kc || null;
  const lvl = kc && kc.level ? appendItemLevel(kc.level) : null;
  modalTitle.value = lvl ? `Spelling Level ${lvl}` : "Spelling Level";
  modalBodyHtml.value = getLevelHtml(lvl);
  showLevelIcon.value = true;
  showLevelModal.value = true;
}

function openWordModalFor(kc: any) {
  activeKcForModal.value = kc || null;
  const word = kc && kc.word ? String(kc.word) : null;
  modalTitle.value = word ? `Spell the word: <strong>${word}</strong>` : "Spelling Word";
  modalBodyHtml.value = "...";
  showLevelIcon.value = true;
  showLevelModal.value = true;
}

function getLevelHtml(level: number | null) {
  switch (Number(level)) {
    case 1:
      return i18n.global.t("lingo_v2.spelling.level_1_description").toString();
    case 2:
      return i18n.global.t("lingo_v2.spelling.level_2_description").toString();
    case 3:
      return i18n.global.t("lingo_v2.spelling.level_3_description").toString();
    case 4:
      return i18n.global.t("lingo_v2.spelling.level_4_description").toString();
    case 5:
      return i18n.global.t("lingo_v2.spelling.level_5_description").toString();
    case 6:
      return i18n.global.t("lingo_v2.spelling.level_6_description").toString();
    case 7:
      return i18n.global.t("lingo_v2.spelling.level_7_description").toString();
    default:
      return i18n.global.t("lingo_v2.spelling.level_1_description").toString();
  }
}

const studentId = computed(() => route.params.studentId as string);
const selectedStudent = computed(() => learnerStore.getLearnerById(studentId.value) || null);

function capitalise(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const displayName = computed(() => {
  const student = selectedStudent.value;
  if (!student) return 'Unknown Student';
  return `${capitalise(student.firstName)} ${capitalise(student.lastName)}`.trim();
});

const studentLingoData = computed(() => 
  lingoStore.getLingoDataByStudentId(studentId.value)
);

const vocabularyPercentage = computed(() => {
  const mastered = studentLingoData.value?.currentVocabularyMasteredCount || 0;
  const total = studentLingoData.value?.currentVocabularyTotalCount || 0;
  if (total === 0) return 0;
  return Math.round((mastered / total) * 100);
});

const vocabularyMasteryDisplay = computed(() => {
  const mastered = studentLingoData.value?.currentVocabularyMasteredCount || 0;
  const total = studentLingoData.value?.currentVocabularyTotalCount || 0;
  return `${mastered}/${total}`;
});

const vocabularyKCs = computed(() => {
  // Use currentVocabularyActivityKcs for the list (current activity words)
  if (studentLingoData.value && Array.isArray(studentLingoData.value.currentVocabularyActivityKcs)) {
    return studentLingoData.value.currentVocabularyActivityKcs;
  }
  return [];
});
function appendItemLevel(level) {
  const levelNum = Number(level);
  if (levelNum === 7) {
    return levelNum + "+";
  }
  return level;
}

onMounted(async () => {
  if (!studentLingoData.value) {
    await lingoStore.fetchStudentLingoData({ 
      studentIds: [studentId.value] 
    });
  }
});

return (_ctx: any,_cache: any) => {
  const _component_page_title = _resolveComponent("page-title")!
  const _component_spinner_loader = _resolveComponent("spinner-loader")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_page_title, {
      pages: pages.value,
      hideFilters: true,
      highlightLast: true
    }, null, 8, ["pages"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _hoisted_3,
        _createElementVNode("span", _hoisted_4, _toDisplayString(displayName.value), 1)
      ]),
      (_unref(lingoStore).loader?.fetchStudentLingoData)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(_component_spinner_loader, { color: 'text-blue-600' })
          ]))
        : (!studentLingoData.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, _hoisted_8))
          : (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("aside", _hoisted_11, [
                  _createElementVNode("div", _hoisted_12, [
                    _hoisted_13,
                    _createElementVNode("div", _hoisted_14, [
                      _createVNode(LingoProgressCircle, {
                        size: "lgBorder",
                        value: vocabularyPercentage.value,
                        shape: "half",
                        color: "#fbbf24",
                        caption: "Mastered",
                        "display-value": vocabularyMasteryDisplay.value,
                        "show-percent-sign": false
                      }, null, 8, ["value", "display-value"])
                    ]),
                    _createVNode(ProgressStatsCard, { stats: progressStats.value }, null, 8, ["stats"])
                  ])
                ]),
                _createElementVNode("main", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", _hoisted_17, [
                      _hoisted_18,
                      _createVNode(LingoDataTable, {
                        items: vocabularyKCs.value,
                        columns: cols.value,
                        "row-key": "id",
                        class: "my-vocabulary-data-table"
                      }, {
                        "cell-word": _withCtx(({ item }) => [
                          _createVNode(_component_router_link, {
                            class: "text-blue open-modal",
                            to: `/main/lingo-vocabulary-word/${studentId.value}/${encodeURIComponent(String(item.strapiWordId ?? item._id ?? item.id ?? item.word ?? ''))}`,
                            "aria-label": "Open vocabulary word details"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(" Know the word: "),
                              _createElementVNode("span", _hoisted_19, _toDisplayString(item.word || "-"), 1)
                            ]),
                            _: 2
                          }, 1032, ["to"])
                        ]),
                        "cell-level": _withCtx(({ item }) => [
                          _createElementVNode("span", {
                            class: "text-blue open-modal",
                            onClick: ($event: any) => (openLevelModalFor(item))
                          }, " Level " + _toDisplayString(appendItemLevel(item.level) ?? "-"), 9, _hoisted_20)
                        ]),
                        "cell-status": _withCtx(({ item }) => [
                          _createElementVNode("span", _hoisted_21, _toDisplayString(getKcData(item.status, "label")), 1),
                          (_unref(getKcStatusTooltip)(item.status))
                            ? (_openBlock(), _createBlock(LingoTooltipIcon, {
                                key: 0,
                                text: _unref(getKcStatusTooltip)(item.status),
                                placement: "top",
                                class: "margin-top-1",
                                ariaLabel: getKcData(item.status, 'label')
                              }, null, 8, ["text", "ariaLabel"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 8, ["items", "columns"])
                    ])
                  ])
                ])
              ])
            ]))
    ]),
    _createVNode(LingoModal, {
      modelValue: showLevelModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showLevelModal).value = $event)),
      title: modalTitle.value,
      icon: showLevelIcon.value ? _unref(BuildingIcon) : '',
      "show-close-button": true,
      centered: ""
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", { innerHTML: modalBodyHtml.value }, null, 8, _hoisted_22)
      ]),
      footer: _withCtx(() => [
        _createElementVNode("button", {
          class: "close-modal-btn",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showLevelModal.value = false))
        }, "Close")
      ]),
      _: 1
    }, 8, ["modelValue", "title", "icon"])
  ], 64))
}
}

})